// Any emoji, mildly pixelated (IDEATION → MVP 6). Device role.
//
// The emoji is drawn with the system emoji font onto a 16×16 canvas and handed
// back as a data URL, which <PixelEmoji> shows scaled up with
// `image-rendering: pixelated`. The real character is never replaced — it stays
// in the DOM as the accessible name — this is only its picture.
//
// Monochrome LCDs get a 20% multiply of the glass colour so the emoji sits ON
// the screen rather than floating over it; the colour screen passes no tint.
// Results are cached per (character, tint) for the life of the page.
//
// Everything fails soft: no document, no canvas, or a canvas that drew nothing
// (no emoji font, jsdom) returns null and the caller shows the plain character.

export const EMOJI_GRID = 16;

const cache = new Map();

// Colour glyph fonts first; the generic families catch whatever the OS has.
const EMOJI_FONT = [
  '"Apple Color Emoji"', '"Segoe UI Emoji"', '"Noto Color Emoji"',
  '"Twemoji Mozilla"', '"EmojiOne Color"', 'sans-serif',
].join(', ');

let canvasOk = null;

function makeCanvas(size) {
  if (typeof document === 'undefined') return null;
  try {
    const c = document.createElement('canvas');
    c.width = size;
    c.height = size;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    return ctx ? { c, ctx } : null;
  } catch {
    return null;
  }
}

/** A CSS custom property (`--lcd-glass`) resolved to a colour string, or ''. */
export function cssColor(name) {
  if (!name || typeof document === 'undefined' || typeof getComputedStyle !== 'function') return '';
  try {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  } catch {
    return '';
  }
}

const SCRATCH = 128;          // scratch canvas side
const SCRATCH_FONT = 64;      // glyph size on it: half the side, room all round

/** `char` drawn at SCRATCH_FONT px in the middle of a SCRATCH canvas, or null. */
function drawLarge(char) {
  const made = makeCanvas(SCRATCH);
  if (!made) return null;
  const { ctx } = made;
  ctx.clearRect(0, 0, SCRATCH, SCRATCH);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `${SCRATCH_FONT}px ${EMOJI_FONT}`;
  // Only reached for a monochrome fallback glyph; colour emoji ignore it.
  ctx.fillStyle = cssColor('--lcd-ink') || 'black';
  ctx.fillText(char, SCRATCH / 2, SCRATCH / 2);
  return made;
}

/** The bounding box of every non-transparent pixel, or null when none. */
function inkBox(ctx, w, h) {
  let data;
  try {
    ({ data } = ctx.getImageData(0, 0, w, h));
  } catch {
    return { x: 0, y: 0, w, h }; // cannot read back: use the whole canvas
  }
  let x0 = w; let y0 = h; let x1 = -1; let y1 = -1;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      if (data[(y * w + x) * 4 + 3] > 8) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  if (x1 < 0) return null;
  return { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/**
 * Draw `char` at `size` px square. Returns the canvas, or null when nothing was
 * drawn. `tint` is a CSS colour string (already resolved) or ''.
 */
export function drawEmoji(char, { size = EMOJI_GRID, tint = '' } = {}) {
  if (canvasOk === false) return null;
  const made = makeCanvas(size);
  if (!made) { canvasOk = false; return null; }
  const { c, ctx } = made;
  ctx.clearRect(0, 0, size, size);

  // Draw big on a roomy scratch canvas, find where the ink actually landed,
  // then scale that box into the cell with a 1px margin, centred. Glyphs are
  // not the same width everywhere — iOS's Apple Color Emoji ❤️ is wider than
  // its advance and a centre-anchored draw straight into 16×16 sliced its
  // left edge off. Measuring the pixels (not measureText, which varies by
  // engine) keeps every emoji whole on every platform.
  const big = drawLarge(char);
  if (!big) return null;
  const box = inkBox(big.ctx, big.c.width, big.c.height);
  if (!box) return null;

  const margin = Math.max(1, Math.round(size / EMOJI_GRID));
  const room = size - margin * 2;
  const scale = Math.min(room / box.w, room / box.h);
  const w = box.w * scale;
  const h = box.h * scale;
  ctx.imageSmoothingEnabled = true;
  if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(big.c, box.x, box.y, box.w, box.h, (size - w) / 2, (size - h) / 2, w, h);
  canvasOk = true;

  if (tint) {
    // 20% multiply of the glass, then clipped back to the glyph's own alpha
    // so the transparent cell stays transparent.
    const shape = makeCanvas(size);
    if (shape) {
      shape.ctx.drawImage(c, 0, 0);
      ctx.globalCompositeOperation = 'multiply';
      ctx.globalAlpha = 0.2;
      ctx.fillStyle = tint;
      ctx.fillRect(0, 0, size, size);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'destination-in';
      ctx.drawImage(shape.c, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
    }
  }
  return c;
}

/**
 * The pixelated emoji as a data URL, cached. `tintVar` is a CSS custom
 * property name (`--lcd-glass`) or '' for none. Null → show the character.
 */
export function emojiDataUrl(char, tintVar = '') {
  const key = `${tintVar}\u0000${char}`;
  if (cache.has(key)) return cache.get(key);
  let url = null;
  const canvas = drawEmoji(char, { tint: cssColor(tintVar) });
  if (canvas) {
    try { url = canvas.toDataURL('image/png'); } catch { url = null; }
  }
  // Cache misses as well, so a failing environment does not retry per render.
  cache.set(key, url);
  return url;
}

/** For tests and the text-size change: forget everything. */
export function clearEmojiCache() {
  cache.clear();
}
